import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import useSEO from '../hooks/useSEO';
import { FaFileContract, FaShieldAlt, FaUserCheck, FaBalanceScale } from 'react-icons/fa';

function TermsOfService() {
  // Apply SEO metadata
  useSEO({
    title: "Terms of Service - GLabs Automator",
    description: "Read the Terms of Service for GLabs Automator Chrome extension. Learn about usage rights, limitations, and your responsibilities.",
    keywords: "terms of service, legal, usage agreement, GLabs Automator",
    openGraph: {
      type: "website"
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: <FaFileContract className="text-4xl text-white" />,
      color: "bg-[#4285F4]",
      title: "License and Use",
      id: "license"
    },
    {
      icon: <FaShieldAlt className="text-4xl text-white" />,
      color: "bg-[#EA4335]",
      title: "Disclaimer",
      id: "disclaimer"
    },
    {
      icon: <FaUserCheck className="text-4xl text-white" />,
      color: "bg-[#FBBC04]",
      title: "Your Responsibilities",
      id: "responsibilities"
    },
    {
      icon: <FaBalanceScale className="text-4xl text-white" />,
      color: "bg-[#34A853]",
      title: "Liability",
      id: "liability"
    }
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <CustomCursor />
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gray-50 dark:bg-gray-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#4285F4]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse bg-[#EA4335]" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center animate-fadeInUp">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Terms of <span className="text-[#4285F4]">Service</span>
            </h1>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-4">
              Last Updated: December 5, 2024
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Please read these terms carefully before using GLabs Automator
            </p>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-12 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sections.map((section, index) => (
              <a
                key={index}
                href={`#${section.id}`}
                className="glass-card p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 text-center group ripple-effect animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`${section.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  {section.icon}
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white">{section.title}</h3>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card rounded-3xl p-8 md:p-12 shadow-2xl space-y-12 animate-fadeInUp">
            
            {/* Introduction */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#4285F4]">📋</span> Introduction
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Welcome to GLabs Automator, a Chrome extension that provides bulk content generation capabilities for Google Labs AI tools including ImageFX, Whisk, MusicFX, and Flow (VEO3). By installing and using our extension, you agree to be bound by these Terms of Service ("Terms").
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                Please read these Terms carefully before using our Extension. If you disagree with any part of these Terms, you may not use our Extension.
              </p>
            </div>

            {/* License and Use */}
            <div id="license">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#4285F4]">🔑</span> License and Use of the Extension
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to use our Extension solely for your personal or commercial purposes. This license is specific to you and may not be sublicensed or assigned.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg text-gray-700 dark:text-gray-300 ml-4">
                <li>Modify, disassemble, decompile or reverse engineer the Extension</li>
                <li>Use the Extension for any illegal purpose or in violation of any local, state, national, or international law</li>
                <li>Remove, circumvent, disable, damage or otherwise interfere with security-related features of the Extension</li>
                <li>Attempt to gain unauthorized access to the Extension or its related systems or networks</li>
                <li>Use the Extension in a manner that could disable, overburden, damage, or impair it</li>
              </ul>
            </div>

            {/* Relationship with Google */}
            <div id="responsibilities">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#EA4335]">🤝</span> Relationship with Google Labs
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Our Extension is designed to work with Google Labs AI tools (ImageFX, Whisk, MusicFX, Flow/VEO3) but is not affiliated with, endorsed by, or in any way connected to Google Inc. or Google Labs. By using our Extension, you acknowledge that:
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg text-gray-700 dark:text-gray-300 ml-4">
                <li>You must comply with Google's Terms of Service when using Google Labs tools</li>
                <li>Any content generated through our Extension is subject to Google's licensing terms</li>
                <li>We have no control over changes Google may make to their services which could affect our Extension's functionality</li>
                <li>You use our Extension with Google Labs tools at your own risk</li>
              </ul>
            </div>

            {/* Intellectual Property */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#FBBC04]">©️</span> Intellectual Property Rights
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                The Extension, including all of its content, features, and functionality (including but not limited to text, graphics, software, and code), is owned by us and is protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                Any feedback, comments, or suggestions you may provide regarding the Extension is entirely voluntary, and we will be free to use such feedback, comments, or suggestions as we see fit without any obligation to you.
              </p>
            </div>

            {/* Content Generated */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#34A853]">🎨</span> Content Generated Using the Extension
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Images, videos, music, and other content generated using our Extension in conjunction with Google Labs tools are subject to Google's terms regarding content ownership and licensing. We make no claims regarding the ownership of such content.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                You are solely responsible for any content you create using our Extension and for ensuring that such content does not infringe upon or violate the rights of any third party.
              </p>
            </div>

            {/* Disclaimer */}
            <div id="disclaimer">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#EA4335]">⚠️</span> Disclaimer of Warranties
              </h2>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-[#FBBC04] p-6 rounded-lg">
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 font-semibold">
                  THE EXTENSION IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  WE DO NOT WARRANT THAT THE EXTENSION WILL FUNCTION UNINTERRUPTED, SECURE, OR AVAILABLE AT ANY PARTICULAR TIME OR LOCATION; THAT ANY ERRORS OR DEFECTS WILL BE CORRECTED; OR THAT THE EXTENSION IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
                </p>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div id="liability">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#4285F4]">⚖️</span> Limitation of Liability
              </h2>
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-[#EA4335] p-6 rounded-lg">
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER INCLUDING, WITHOUT LIMITATION, DAMAGES FOR LOSS OF USE, DATA, OR PROFITS, ARISING OUT OF OR IN ANY WAY CONNECTED WITH THE USE OR PERFORMANCE OF THE EXTENSION.
                </p>
              </div>
            </div>

            {/* Changes to Terms */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#FBBC04]">🔄</span> Changes to Terms of Service
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. When we make changes, we will update the "Last Updated" date at the top of this page. Your continued use of the Extension after any modifications to the Terms constitutes your acceptance of the revised Terms.
              </p>
            </div>

            {/* Termination */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#EA4335]">🚫</span> Termination
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may terminate or suspend your access to the Extension immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-[#34A853]">📧</span> Contact Us
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                If you have any questions about these Terms, please contact us at:
              </p>
              <a
                href="https://intercom.help/connectautoplaylabs/en"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#4285F4] to-[#357ae8] text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl ripple-effect"
              >
                Support Center
              </a>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-12 animate-fadeInUp" style={{ animationDelay: '200ms' }}>
            <Link to="/">
              <button className="glass-card px-8 py-4 rounded-2xl font-bold text-gray-900 dark:text-white hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl ripple-effect">
                ← Back to Home
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default TermsOfService;